import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDirection } from "../../../../hooks/useDirection";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from "../../../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../ui/tooltip";
import { TooltipProvider } from "../../../ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../ui/popover";
import {
  Search,
  Plus,
  Sparkles,
  Settings,
  SortAsc,
  LayoutGrid,
  Clock,
  BarChart3,
  Star,
  Filter,
  ChevronDown,
  Brain,
} from "lucide-react";
import { ToolbarProps, InsightSortOption, InsightViewType } from "./types";
import { cn } from "../../../../lib/utils";

const Toolbar: React.FC<ToolbarProps> = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  sourceFilter,
  onSourceFilterChange,
  sortOption,
  onSortChange,
  viewType,
  onViewTypeChange,
  categories,
  showFavoritesOnly,
  onShowFavoritesOnlyChange,
  onManageCategories,
  onAddInsight,
  onAIExtract,
  onAISynthesis,
  totalCount,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  const sortOptions: { value: InsightSortOption; label: string }[] = [
    { value: "newest", label: t("newest_first") || "Newest First" },
    { value: "oldest", label: t("oldest_first") || "Oldest First" },
    { value: "favorites_first", label: t("favorites_first") || "Favorites First" },
    { value: "most_connected", label: t("most_connected") || "Most Connected" },
  ];

  const viewTypes: { value: InsightViewType; icon: React.ReactNode; label: string }[] = [
    { value: "cards", icon: <LayoutGrid className="h-4 w-4" />, label: t("card_view") || "Cards" },
    { value: "timeline", icon: <Clock className="h-4 w-4" />, label: t("timeline_view") || "Timeline" },
    { value: "stats", icon: <BarChart3 className="h-4 w-4" />, label: t("stats_view") || "Stats" },
  ];

  const hasActiveFilters =
    statusFilter !== "all" ||
    categoryFilter !== "all" ||
    sourceFilter !== "all" ||
    showFavoritesOnly;

  return (
    <>
      <div className="flex flex-col gap-2 sm:gap-3 pb-3 sm:pb-4 border-b mb-3 sm:mb-4">
        {/* Single row: Search + Filters + AI Tools + Add */}
        <div className="flex flex-row gap-2 items-center sm:justify-between">
          {/* Search — constrained width on desktop */}
          <div className="relative flex-1 sm:max-w-md min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t("search_insights")}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 h-9 bg-muted/50 border-0 focus-visible:ring-1 focus-visible:ring-primary/50 transition-all"
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 items-center flex-shrink-0">
            {/* Mobile filter button — shown only on mobile */}
            <Button
              size="sm"
              variant="outline"
              className="sm:hidden h-9 w-9 p-0 flex-shrink-0"
              onClick={() => setShowFilterSheet(true)}
            >
              <Filter className="h-4 w-4" />
            </Button>

            {/* Desktop filter popover — replaces inline Row 2 filters */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="hidden sm:flex h-9 px-3 flex-shrink-0 relative"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {t("filters", "Filters")}
                  {(hasActiveFilters || sortOption !== "newest") && (
                    <span className="absolute -top-1 -end-1 w-2.5 h-2.5 bg-primary rounded-full border-2 border-background" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-0">
                <div className="p-3 pb-2 border-b">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">{t("filters", "Filters")}</h4>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                        {totalCount} {t("items") || "items"}
                      </div>
                      {(hasActiveFilters || sortOption !== "newest") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            onStatusFilterChange("all");
                            onCategoryFilterChange("all");
                            onSourceFilterChange("all");
                            onShowFavoritesOnlyChange(false);
                            onSortChange("newest");
                          }}
                        >
                          {t("clear_all", "Clear")}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-3 space-y-3 max-h-96 overflow-y-auto">
                  {/* View mode */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("view_mode") || "View"}</div>
                    <div className="flex rounded-lg bg-muted p-0.5 w-fit">
                      {viewTypes.map((vt) => (
                        <Button
                          key={vt.value}
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "h-7 px-2.5 rounded-md text-xs",
                            viewType === vt.value
                              ? "bg-background shadow-sm"
                              : "hover:bg-transparent"
                          )}
                          onClick={() => onViewTypeChange(vt.value)}
                        >
                          {vt.icon}
                          <span className="ml-1.5">{vt.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Sort */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("sort_by") || "Sort by"}</div>
                    <Select value={sortOption} onValueChange={(v) => onSortChange(v as InsightSortOption)}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sortOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("status")}</div>
                    <Select value={statusFilter} onValueChange={onStatusFilterChange}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all_statuses") || "All"}</SelectItem>
                        <SelectItem value="pending">{t("active")}</SelectItem>
                        <SelectItem value="archived">{t("archived")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Category */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("category")}</div>
                    <Select value={categoryFilter} onValueChange={onCategoryFilterChange}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all_categories") || "All Categories"}</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat._id} value={cat._id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: cat.color }}
                              />
                              <span className="truncate">{cat.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Source */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("source")}</div>
                    <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all_sources") || "All Sources"}</SelectItem>
                        <SelectItem value="user_created">{t("personal") || "Personal"}</SelectItem>
                        <SelectItem value="engagement">{t("from_engagements") || "From Engagements"}</SelectItem>
                        <SelectItem value="mentor_assigned">{t("from_mentor") || "From Mentor"}</SelectItem>
                        <SelectItem value="sketchbook">{t("ai_generated") || "AI Generated"}</SelectItem>
                        <SelectItem value="reflection">{t("source_reflection") || "Daily reflection"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Favorites Toggle */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("favorites") || "Favorites"}</div>
                    <Button
                      variant={showFavoritesOnly ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "w-full h-8 text-xs",
                        showFavoritesOnly && "bg-amber-500 hover:bg-amber-600"
                      )}
                      onClick={() => onShowFavoritesOnlyChange(!showFavoritesOnly)}
                    >
                      <Star className={cn("h-3.5 w-3.5 mr-1.5", showFavoritesOnly && "fill-current")} />
                      {showFavoritesOnly
                        ? (t("showing_favorites_only") || "Showing favorites only")
                        : (t("show_favorites_only") || "Show favorites only")}
                    </Button>
                  </div>

                  {/* Manage categories */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-full justify-start text-xs text-muted-foreground hover:text-foreground"
                    onClick={onManageCategories}
                  >
                    <Settings className="h-3.5 w-3.5 mr-1.5" />
                    {t("manage_categories")}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* AI Tools dropdown — merges AI Extract + AI Synthesis */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 w-9 p-0 sm:w-auto sm:px-4 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200 text-violet-700 hover:from-violet-100 hover:to-purple-100 hover:border-violet-300 flex-shrink-0"
                >
                  <Sparkles className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t("ai_tools", "AI")}</span>
                  <ChevronDown className="h-3 w-3 ml-1 hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onAIExtract}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t("ai_extract") || "Extract from text"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAISynthesis}>
                  <Brain className="h-4 w-4 mr-2" />
                  {t("ai_synthesis") || "Synthesize insights"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Add Insight */}
            <Button
              size="sm"
              className="h-9 w-9 p-0 sm:w-auto sm:px-4 bg-amber-500 hover:bg-amber-600 shadow-sm flex-shrink-0"
              onClick={onAddInsight}
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t("add_insight")}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Filter Sheet */}
      <Sheet open={showFilterSheet} onOpenChange={setShowFilterSheet}>
        <SheetContent side={isRTL ? "left" : "right"} className="w-[85vw] sm:w-[400px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              {t("filters_and_view") || "Filters & View"}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* View Mode */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("view_mode") || "View Mode"}</label>
              <div className="flex rounded-lg bg-muted p-1">
                {viewTypes.map((vt) => (
                  <Button
                    key={vt.value}
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "flex-1 h-9",
                      viewType === vt.value && "bg-background shadow-sm"
                    )}
                    onClick={() => onViewTypeChange(vt.value)}
                  >
                    {vt.icon}
                    <span className="ml-2 text-xs">{vt.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("sort_by") || "Sort by"}</label>
              <Select value={sortOption} onValueChange={(v) => onSortChange(v as InsightSortOption)}>
                <SelectTrigger className="w-full">
                  <SortAsc className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("status")}</label>
              <Select value={statusFilter} onValueChange={onStatusFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_statuses") || "All"}</SelectItem>
                  <SelectItem value="pending">{t("active")}</SelectItem>
                  <SelectItem value="archived">{t("archived")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("category")}</label>
              <Select value={categoryFilter} onValueChange={onCategoryFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_categories") || "All Categories"}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("source")}</label>
              <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_sources") || "All Sources"}</SelectItem>
                  <SelectItem value="user_created">{t("personal") || "Personal"}</SelectItem>
                  <SelectItem value="engagement">{t("from_engagements") || "From Engagements"}</SelectItem>
                  <SelectItem value="mentor_assigned">{t("from_mentor") || "From Mentor"}</SelectItem>
                  <SelectItem value="sketchbook">{t("ai_generated") || "AI Generated"}</SelectItem>
                  <SelectItem value="reflection">{t("source_reflection") || "Daily reflection"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Favorites Toggle */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("favorites") || "Favorites"}</label>
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                className={cn(
                  "w-full",
                  showFavoritesOnly && "bg-amber-500 hover:bg-amber-600"
                )}
                onClick={() => onShowFavoritesOnlyChange(!showFavoritesOnly)}
              >
                <Star className={cn("h-4 w-4 mr-2", showFavoritesOnly && "fill-current")} />
                {showFavoritesOnly
                  ? (t("showing_favorites_only") || "Showing favorites only")
                  : (t("show_favorites_only") || "Show favorites only")}
              </Button>
            </div>

            {/* Manage Categories */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setShowFilterSheet(false);
                onManageCategories();
              }}
            >
              <Settings className="h-4 w-4 mr-2" />
              {t("manage_categories")}
            </Button>

            {/* Item Count */}
            <div className="pt-4 border-t">
              <div className="text-sm text-center">
                <span className="font-medium">{totalCount}</span>
                <span className="text-muted-foreground ml-1">{t("items") || "items"}</span>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Toolbar;
